import {z} from 'zod';

import {CampaignFilterSchema} from '@dms/api/shared';

// Catalog of the attributes a vehicle selection can filter by, and which operators each one
// supports: the FE drives the "everything except" (`excludeField`) and "value is missing"
// (`supportsMissing`) controls from it instead of hard-coding the attribute list.
export const VehicleSelectionAttributeResponseBodySchema = z.object({
  attribute: z.string(),
  valueType: z.enum(['CODE', 'UUID', 'INTEGER', 'DATE']),
  includeKind: z.enum(['LIST', 'RANGE', 'FROM', 'GREATER_THAN', 'SINGLE']),
  includeFields: z.array(z.string()),
  excludeField: z.string().nullable(),
  supportsMissing: z.boolean(),
  unit: z.enum(['MINOR_UNITS', 'DAYS']).nullable(),
});
export type VehicleSelectionAttributeResponseBody = z.infer<
  typeof VehicleSelectionAttributeResponseBodySchema
>;

export const GetVehicleSelectionAttributesApiResponseSchema = z.array(
  VehicleSelectionAttributeResponseBodySchema
);
export type GetVehicleSelectionAttributesApiResponse = z.infer<
  typeof GetVehicleSelectionAttributesApiResponseSchema
>;

// Read-only preview of a promotional photo's draft selection against the active fleet. Nothing is
// assigned: only saving the photo sends the selection.
export const PreviewPromotionalPhotoVehicleSelectionRequestBodySchema = z.object({
  // null for a photo that is not saved yet; then nothing is "removed" and every match is "added".
  promotionalPhotoId: z.string().uuid().nullable(),
  vehicleSelection: CampaignFilterSchema.nullable(),
  autoAssign: z.boolean(),
  // Items per list (1..200, BE default 50); the counts always cover the whole scan.
  limit: z.number().int().min(1).max(200).optional(),
});
export type PreviewPromotionalPhotoVehicleSelectionRequestBody = z.infer<
  typeof PreviewPromotionalPhotoVehicleSelectionRequestBodySchema
>;

export const VehicleSelectionOutcomeOperatorSchema = z.enum([
  'INCLUDED_IN',
  'EXCLUDED_FROM',
  'BETWEEN',
  'GREATER_THAN',
  'ON_OR_AFTER',
  'MISSING',
]);
export type VehicleSelectionOutcomeOperator = z.infer<typeof VehicleSelectionOutcomeOperatorSchema>;

// `expected` is what the condition asks for (a list, a {from, to} range, a number, a date or null)
// and `actual` what the listing has (a value, a list or null when missing); both are rendered, never
// interpreted, so they stay untyped JSON.
export const VehicleSelectionOutcomeResponseBodySchema = z.object({
  attribute: z.string(),
  operator: VehicleSelectionOutcomeOperatorSchema,
  expected: z.unknown(),
  actual: z.unknown(),
  matched: z.boolean(),
});
export type VehicleSelectionOutcomeResponseBody = z.infer<
  typeof VehicleSelectionOutcomeResponseBodySchema
>;

export const VehicleSelectionPreviewItemResponseBodySchema = z.object({
  saleVehicleId: z.string().uuid(),
  vehicleId: z.string().uuid(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  branchId: z.string().uuid().nullable(),
  outcomes: z.array(VehicleSelectionOutcomeResponseBodySchema),
});
export type VehicleSelectionPreviewItemResponseBody = z.infer<
  typeof VehicleSelectionPreviewItemResponseBodySchema
>;

export const VehicleSelectionPreviewCountsResponseBodySchema = z.object({
  added: z.number().int(),
  removed: z.number().int(),
  unchanged: z.number().int(),
  notEligible: z.number().int(),
  // Listings the photo was removed from by hand; they are never re-added automatically.
  manuallyRemoved: z.number().int(),
  scanned: z.number().int(),
});
export type VehicleSelectionPreviewCountsResponseBody = z.infer<
  typeof VehicleSelectionPreviewCountsResponseBodySchema
>;

export const VehicleSelectionPreviewResponseBodySchema = z.object({
  restricted: z.boolean(),
  autoAssign: z.boolean(),
  counts: VehicleSelectionPreviewCountsResponseBodySchema,
  // The scan stopped at its cap before covering the whole active fleet.
  truncated: z.boolean(),
  added: z.array(VehicleSelectionPreviewItemResponseBodySchema),
  removed: z.array(VehicleSelectionPreviewItemResponseBodySchema),
});
export type VehicleSelectionPreviewResponseBody = z.infer<
  typeof VehicleSelectionPreviewResponseBodySchema
>;

export type PreviewPromotionalPhotoVehicleSelectionApiArg = {
  previewPromotionalPhotoVehicleSelectionRequestBody: PreviewPromotionalPhotoVehicleSelectionRequestBody;
};
export type PreviewPromotionalPhotoVehicleSelectionApiResponse =
  VehicleSelectionPreviewResponseBody;
